// D1Pricing.jsx
// Editorial-direction pricing. Same three plans (Free / Core / Premium)
// and same data as the old Pricing.jsx, but with serif prices, mono eyebrows,
// hairline rules instead of card boxes, and a roman-numeral plan index.
// The "Core" plan gets an orange frame + "Launch pricing" banner.

import React from 'react';
import { D1 } from './d1-tokens';

const PLANS = [
  {
    n: 'i', name: 'Free', price: '$0', unit: 'forever',
    note: null, banner: null, trial: null, featured: false,
    features: [
      '3 exercises per day across Reading, Listening, Pronunciation, and Fill-in-the-Blank',
    ],
    roadmap: null,
  },
  {
    n: 'ii', name: 'Core', price: '$6', unit: '/ month',
    note: '$10/mo after launch period',
    banner: 'Launch pricing',
    trial: null,
    featured: true,
    features: [
      'Unlimited Reading, Listening, Pronunciation, and Fill-in-the-Blank',
      '10 Translation exercises / month',
      'Vocabulary Topics',
    ],
    roadmap: null,
  },
  {
    n: 'iii', name: 'Premium', price: '$13', unit: '/ month',
    note: '$20/mo when fully launched',
    banner: null,
    trial: '1 week free trial',
    featured: false,
    features: [
      'Everything in Core',
      '100 Translation exercises / month',
      'Ask Flash — embedded AI assistant',
    ],
    roadmap: [
      'Content-to-Curriculum',
      'AI-assisted Writing',
      'Conversation practice',
    ],
  },
];

export default function D1Pricing() {
  return (
    <section id="pricing" style={s.section}>
      <div className="d1-price-header" style={s.header}>
        <div className="d1-eyebrow">§ Pricing</div>
        <h2 className="d1-serif d1-price-h2" style={s.h2}>
          Start free. <span style={{ color: D1.orange }}>Upgrade when you're ready.</span>
        </h2>
      </div>

      <div className="d1-price-grid" style={s.grid}>
        {PLANS.map((p) => (
          <article
            key={p.name}
            style={{ ...s.card, ...(p.featured ? s.cardFeatured : null) }}
          >
            {p.banner && <div style={s.banner}>{p.banner}</div>}

            <div style={s.cardInner}>
              <div style={s.headRow}>
                <span className="d1-num" style={s.num}>{p.n}</span>
                <h3 style={s.name}>{p.name}</h3>
              </div>

              <div style={s.priceRow}>
                <span className="d1-serif" style={s.price}>{p.price}</span>
                <span style={s.unit}>{p.unit}</span>
              </div>
              {p.note && <div style={s.note}>{p.note}</div>}

              <div style={s.hr} />

              <ul style={s.features}>
                {p.features.map((f, i) => (
                  <li key={i} style={s.feature}>
                    <span style={s.check}>✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {p.roadmap && (
                <>
                  <div style={s.roadmapLabel}>Coming soon</div>
                  <ul style={s.features}>
                    {p.roadmap.map((r, i) => (
                      <li key={i} style={{ ...s.feature, ...s.featureDim }}>
                        <span style={{ ...s.check, color: D1.faint }}>◇</span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {p.trial && <div style={s.trial}>{p.trial}</div>}
            </div>
          </article>
        ))}
      </div>

      <style>{`
        .d1-price-header { grid-template-columns: 220px 1fr; }
        .d1-price-h2     { font-size: clamp(28px, 3.4vw, 40px); margin: 0; line-height: 1.1; max-width: 720px; }
        .d1-price-grid   { grid-template-columns: repeat(3, 1fr); gap: 16px; }
        @media (max-width: 900px) {
          .d1-price-header { grid-template-columns: 1fr; gap: 16px !important; }
          .d1-price-grid   { grid-template-columns: 1fr; max-width: 420px; }
        }
      `}</style>
    </section>
  );
}

const s = {
  section:  { padding: 'clamp(64px, 9vw, 96px) var(--pad-x)', borderTop: `1px solid ${D1.rule}` },
  header:   { display: 'grid', gap: 48, marginBottom: 40 },
  h2:       { color: D1.ink, letterSpacing: '-0.02em' },
  grid:     { display: 'grid' },
  card: {
    border: `1px solid ${D1.rule}`, borderRadius: 4, background: D1.bg,
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  cardFeatured: {
    border: `1px solid rgba(255,138,61,0.35)`,
    boxShadow: '0 0 0 1px rgba(255,138,61,0.12), 0 12px 40px rgba(255,138,61,0.04)',
  },
  banner: {
    background: D1.orange, color: '#1a1210',
    fontFamily: D1.mono, fontSize: 10, fontWeight: 700,
    letterSpacing: '0.1em', textTransform: 'uppercase',
    textAlign: 'center', padding: '6px 0',
  },
  cardInner: { padding: '24px 22px', display: 'flex', flexDirection: 'column', flexGrow: 1 },
  headRow:   { display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 },
  num:       { fontSize: 22 },
  name: {
    fontFamily: D1.sans, fontSize: 16, fontWeight: 600, margin: 0,
    color: D1.ink, letterSpacing: '-0.01em',
  },
  priceRow: { display: 'flex', alignItems: 'baseline', gap: 6 },
  price: {
    fontSize: 44, fontWeight: 500, color: D1.ink,
    letterSpacing: '-0.03em', lineHeight: 1,
  },
  unit: { fontSize: 13, color: D1.dim, fontFamily: D1.sans },
  note: { fontSize: 11, color: D1.faint, marginTop: 6 },
  hr:   { height: 1, background: D1.rule, margin: '18px 0' },
  features: { listStyle: 'none', padding: 0, margin: 0 },
  feature: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    fontSize: 13, color: D1.ink, lineHeight: 1.5, marginBottom: 10,
  },
  featureDim: { color: D1.dim, fontSize: 12 },
  check: { color: D1.orange, fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 3 },
  roadmapLabel: {
    fontFamily: D1.mono, fontSize: 10, fontWeight: 700, color: D1.dim,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    marginTop: 6, marginBottom: 10,
  },
  trial: {
    marginTop: 'auto', paddingTop: 18,
    fontFamily: D1.mono, fontSize: 11, color: D1.orange,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    textAlign: 'center', borderTop: `1px solid ${D1.rule}`,
  },
};